import { Skeleton } from 'primereact/skeleton';
import { useCallback, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { useSetAtom } from 'jotai';
import { selectWidgetAIAtom } from '~/atoms/dashboardAtom';
import { NoDataAvailableTemplate } from '../../common/NoDataAvailableTemplate';
import { WidgetHeader } from '../../common/WidgetHeader';
import { useWidgetPopout } from '../../../../hooks/useWidgetPopout';
import { useRecommendationReportDownload } from '~/hooks/useRecommendationReportDownload';
import type { RecommendationReport } from '../RecommendationReportWidget/pdfGenerator';
import {
  RiskAlertDetailsDialog,
  type RiskAlertDetails,
} from './RiskAlertDetailsDialog';

interface DataPoint {
  value: string;
  metric: string;
  concern: string;
  threshold: string;
}

interface RiskAlert {
  title: string;
  percentage: string;
  description: string;
  level: 'high' | 'medium' | 'low';
  data_points?: DataPoint[];
  affected_positions?: string[];
  extended_reasoning?: string;
  recommended_action?: string;
}

export type RiskAlertsWidgetProps = {
  riskAlerts: RiskAlert[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  handleRemoveWidget?: (widgetId: string) => void;
  widgetId: string;
  title?: string;
  timestamp?: string;
  snapshotId?: string | null;
  report?: RecommendationReport | null;
  portfolioName?: string | null;
  tickerToCompanyName?: Map<string, string>;
};

export const RiskAlertsWidget: React.FC<RiskAlertsWidgetProps> = ({
  riskAlerts,
  isLoading,
  error,
  refetch,
  handleRemoveWidget,
  widgetId,
  title = 'Risk Alerts',
  timestamp,
  snapshotId,
  report,
  portfolioName,
  tickerToCompanyName,
}) => {
  const removeWidget = useCallback(
    () => handleRemoveWidget?.(widgetId),
    [widgetId, handleRemoveWidget],
  );
  const downloadReport = useRecommendationReportDownload(portfolioName);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<RiskAlertDetails | null>(
    null,
  );
  const [dialogVisible, setDialogVisible] = useState(false);

  const handleExport = useCallback(async () => {
    if (!report) return;
    setIsExporting(true);
    try {
      await downloadReport(report);
    } finally {
      setIsExporting(false);
    }
  }, [downloadReport, report]);

  const handleViewDetails = useCallback((alert: RiskAlert) => {
    const alertDetails: RiskAlertDetails = {
      title: alert.title,
      message: alert.description,
      level: alert.level,
      data_points: alert.data_points,
      affected_positions: alert.affected_positions,
      extended_reasoning: alert.extended_reasoning,
      recommended_action: alert.recommended_action,
    };
    setSelectedAlert(alertDetails);
    setDialogVisible(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogVisible(false);
    setSelectedAlert(null);
  }, []);
  const setSelectWidgetAI = useSetAtom(selectWidgetAIAtom);
  const handlePopout = useWidgetPopout({
    widgetId,
    widgetComponent: RiskAlertsWidget,
    title: title || 'Risk Alerts Widget',
    defaultWidth: 800,
    defaultHeight: 600,
    widgetProps: {
      riskAlerts,
      isLoading,
      error,
      refetch,
      handleRemoveWidget,
      widgetId,
      title,
      timestamp,
      snapshotId,
      report,
      portfolioName,
      tickerToCompanyName,
    },
  });

  const noData = useMemo(() => {
    return !riskAlerts.length;
  }, [riskAlerts]);

  if (isLoading) {
    return (
      <div className="p-3 h-full">
        <Skeleton className="h-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full hover-container" id={widgetId}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          refetch={() => refetch()}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          icon="pi pi-exclamation-triangle"
          title="Something went wrong."
          isError
        />
      </div>
    );
  }

  if (!snapshotId) {
    return (
      <div className="h-full hover-container" id={widgetId}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          icon="pi pi-exclamation-triangle"
          title="No Snapshot Available."
          subTitle="Run portfolio analysis to generate snapshot"
        />
      </div>
    );
  }

  if (noData) {
    return (
      <div className="h-full hover-container" id={widgetId}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          refetch={() => refetch()}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          title="No Risk Alerts Available."
          subTitle="Run portfolio analysis to generate risk alerts"
          icon="pi pi-exclamation-triangle"
        />
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          .risk-alert-view-details-btn.p-button-outlined .p-button-icon {
            color: var(--bq-color-primary) !important;
          }
          .risk-alert-view-details-btn.p-button-outlined:hover .p-button-icon {
            color: var(--bq-color-primary) !important;
          }
        `}
      </style>
      <div className="hover-container h-full flex flex-column" id={widgetId}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          refetch={() => refetch()}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          onExport={report ? handleExport : undefined}
          isExporting={isExporting}
          widgetId={widgetId}
          children={
            timestamp && (
              <span className="ml-2 bq-timestamp-badge">{timestamp}</span>
            )
          }
        />

        {/* Full Height Content with Scrollable Area */}
        <div className="flex-1 p-3 overflow-y-auto">
          <div className="flex flex-column gap-3">
            {riskAlerts.map((alert, index) => (
              <Card
                key={`risk-${index}`}
                className="border-1"
                style={{
                  borderColor:
                    alert.level === 'high'
                      ? 'var(--bq-color-negative)'
                      : alert.level === 'medium'
                        ? 'var(--bq-color-warning-strong)'
                        : 'var(--bq-color-positive)',
                }}
                pt={{
                  body: {
                    className: 'p-3',
                    style: {
                      backgroundColor:
                        alert.level === 'high'
                          ? 'rgba(239, 68, 68, 0.1)'
                          : alert.level === 'medium'
                            ? 'rgba(245, 158, 11, 0.1)'
                            : 'rgba(16, 185, 129, 0.1)',
                      overflow: 'hidden',
                      wordWrap: 'break-word',
                      minHeight: '80px',
                    },
                  },
                  content: {
                    className: 'p-0 m-0',
                    style: {
                      overflow: 'hidden',
                      wordWrap: 'break-word',
                    },
                  },
                }}
              >
                <div className="flex justify-content-between align-items-center mb-2 gap-2">
                  <div
                    className="flex align-items-center gap-2 flex-1"
                    style={{ minWidth: 0 }}
                  >
                    <span
                      className="font-semibold"
                      style={{
                        color: 'var(--bq-text-primary)',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      {alert.title}
                    </span>
                    {(alert.data_points ||
                      alert.affected_positions ||
                      alert.extended_reasoning ||
                      alert.recommended_action) && (
                      <Button
                        label="View Details"
                        icon="pi pi-info-circle"
                        size="small"
                        outlined
                        onClick={() => handleViewDetails(alert)}
                        tooltip="View detailed analysis and recommendations"
                        tooltipOptions={{ position: 'top' }}
                        className="risk-alert-view-details-btn"
                        style={{
                          flexShrink: 0,
                          borderColor: 'var(--bq-color-primary)',
                          color: 'var(--bq-color-primary)',
                        }}
                        pt={{
                          root: {
                            style: {
                              borderColor: 'var(--bq-color-primary)',
                              color: 'var(--bq-color-primary)',
                            },
                          },
                          icon: {
                            style: {
                              color: 'var(--bq-color-primary) !important',
                            },
                          },
                          label: {
                            style: {
                              color: 'var(--bq-color-primary)',
                            },
                          },
                        }}
                      />
                    )}
                  </div>
                  <span
                    className="font-bold"
                    style={{
                      color: 'var(--bq-text-primary)',
                      flexShrink: 0,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {alert.percentage}
                  </span>
                </div>
                <p
                  className="text-sm"
                  style={{
                    color: 'var(--bq-text-secondary)',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    margin: 0,
                  }}
                >
                  {alert.description}
                </p>
              </Card>
            ))}
          </div>
        </div>
        <RiskAlertDetailsDialog
          visible={dialogVisible}
          onHide={handleCloseDialog}
          alert={selectedAlert}
          tickerToCompanyName={tickerToCompanyName}
        />
      </div>
    </>
  );
};
